import { useCallback, useEffect, useRef, useState } from "react";
import { fetchHotWalletReconciliationSummary, type ApiErrorInfo, type ApiHotWalletReconSummary } from "@/lib/api";

interface UseHotWalletReconciliationResult {
  summary: ApiHotWalletReconSummary | null;
  loading: boolean;
  error: ApiErrorInfo | null;
  refresh: () => void;
}

/** Detail hook for HW-S4 (reconciliation). */
export function useHotWalletReconciliation(walletId: string | undefined): UseHotWalletReconciliationResult {
  const [summary, setSummary] = useState<ApiHotWalletReconSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiErrorInfo | null>(null);
  // Only the most recent load may write state. Switching wallet (or refreshing)
  // while an earlier request is still in flight otherwise lets the slower,
  // older response land last and put one wallet's statement on another
  // wallet's page — an out-of-order write, not a network error, so nothing
  // else catches it. The id is bumped before the request, so a response for a
  // superseded wallet is discarded rather than rendered. Same fix as
  // `use-hot-wallet-deposits`.
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    if (!walletId) {
      setSummary(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await fetchHotWalletReconciliationSummary(walletId);
      if (seq !== loadSeq.current) return;
      if (result.error) {
        setSummary(null);
        setError(result.error);
      } else {
        setSummary(result.item);
      }
    } catch {
      if (seq !== loadSeq.current) return;
      setSummary(null);
      setError({ reasonCode: "NETWORK_ERROR" });
    } finally {
      // A superseded load must not clear the spinner the current one owns.
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [walletId]);

  useEffect(() => {
    load();
  }, [load]);

  return { summary, loading, error, refresh: load };
}
